import { isFunction } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { hooks } from '@wove/react';
import { useState } from 'react';
import ReactDOM from 'react-dom';
import { useEffectCustom } from '../../hooks/use-effect-custom';
import { Modal, ModalProps } from '../../modal';
import { bodyAppendDivElement, BodyAppendDivElementProps, removeBodyChild } from '../../utils';

type DialogModalProps = Omit<ModalProps, 'visible' | 'getContainer' | 'onClose' | 'children'> & {
  content: JSX.Element;
};

const ModalRender = (props: DialogModalProps & BodyAppendDivElementProps) => {
  const [visible, setVisible] = useState(false);
  const elementId = props.elementId;

  const onClose = hooks.useCallbackRef(() => {
    setVisible(false);
  });

  useEffectCustom(() => {
    setVisible(true);
    window[`dialogModalClose`] = () => {
      setVisible(false);
    };
    window[`dialogModalClose_${elementId}`] = () => {
      setVisible(false);
    };
  }, []);

  const onAfterClose = hooks.useCallbackRef(() => {
    removeBodyChild(`#${props.elementId}`);
    props.onAfterClose?.();
  });

  return (
    <Modal
      closeByMask={false}
      destroyOnClose={true}
      {...props}
      visible={visible}
      getContainer={props.divElement}
      onClose={onClose}
      onAfterClose={onAfterClose}
      className={classNames('v-dialog-modal', props.className)}
    >
      {props.content}
    </Modal>
  );
};

export const dialogModal = {
  show: (props: DialogModalProps) => {
    const { divElement, elementId } = bodyAppendDivElement();
    ReactDOM.render(<ModalRender {...props} divElement={divElement} elementId={elementId} />, divElement);
    return {
      closeId: elementId,
    };
  },
  close: (params?: { closeId: string }) => {
    const closeKey = params?.closeId ? `dialogModalClose_${params?.closeId}` : 'dialogModalClose';
    const dialogModalClose = window[closeKey];
    if (dialogModalClose && isFunction(dialogModalClose)) {
      window[closeKey]();
      window[closeKey] = null;
    } else {
      const element = document.querySelector('.v-dialog-modal');
      if (element) {
        const elementParent = document.querySelector('.v-dialog-modal')?.parentNode;
        document.body.removeChild(elementParent as Node);
      }
    }
  },
};
